import Ember from 'ember';
import { all } from 'ember-concurrency';
import { dropTask, restartableTask, task } from 'ember-concurrency';
import moment from 'moment-timezone';
import {
  ResourceName as SyncAlertResourceName
} from 'volta/models/order-sync-alert';
import {
  PlanningEvents as PlanningEvents,
  PlanningResourceName as PlanningResourceName
} from 'volta/models/planning';
import PlanningOrder, {
  CreatedEvents,
  DeletedEvents,
  Events as POEvents,
  IPOKeyObject,
  POsLifecycleEvents,
  Queries,
  ResourceName,
  Statuses,
  StatusesOptions,
  VisibleStatuses
} from 'volta/models/planning-order';
import User from 'volta/models/user';
import { normalize } from 'volta/utils/api/serialize-and-push';
import { defaultErrorHandler, IJSONAPIErrors } from 'volta/utils/error-utils';
import {
  btFilter,
  eqFilter,
  gtFilter,
  inFilter,
  ltFilter,
  niFilter
} from 'volta/utils/filters-utils';

import { action } from '@ember/object';
import Service, { inject as service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type RouterService from '@ember/routing/router-service';
import type IntlService from 'ember-intl/services/intl';
import type { TAcquisitionCode } from 'volta/models/constants/acquisition-codes';
import type { IManyPlanningOrdersUpdatedResult, TPOStatus } from 'volta/models/types/planning-order';
import type { TAduFrequency } from 'volta/models/types/planning-settings';
import type BvFlashService from 'volta/services/bv-flash';
import type EventStreamService from 'volta/services/event-stream';
import type LabelService from 'volta/services/label-service';
import type SessionUserService from 'volta/services/session-user';
import type StoreService from 'volta/services/store';
import type { DocWithData, IJsonApiQuery } from 'volta/utils/api/jsonapi-types';
import type { IPlanningCalculationState } from './planning-api';
import type { IBuffer } from 'volta/models/types/planning';
import type { IPOGroup } from 'volta/components/po-workbench';
import type { TUserType } from 'volta/models/types/user';

const NB_PROJECTION_MONTHS = 12;
const CREATION_EVENTS = [
  'PlanningOrdersDrafted',
  'PlanningOrderDrafted',
  'PlanningOrderCreated',
  'PlanningOrdersCreated',
  'PlanningsCalculationDone'
];

export type TDueDateOp = 'eq' | 'gt' | 'lt';

export interface IPOFilters {
  dueDate?: Date;
  sku?: string;
  status: TPOStatus[];
  deliveryWorkshop: string[];
  supplier: string[];
  workshop: string[];
  acqCode: TAcquisitionCode[];
  orderNum: string[];
  orderType: string[];
  onlyPositiveQty?: boolean;
  dueDateOp?: TDueDateOp;
  syncAlert: number[];
  schedulingAlert: number[];
  schedulingAlertPriority: number[];
  planningBufferZone: string[];
  executionBufferZone: string[];
}

export const DEFAULT_FILTERS = {
  dueDate: undefined,
  sku: undefined,
  status: [],
  deliveryWorkshop: [],
  supplier: [],
  workshop: [],
  acqCode: [],
  orderNum: [],
  orderType: [],
  onlyPositiveQty: undefined,
  dueDateOp: undefined,
  syncAlert: [],
  schedulingAlert: [],
  schedulingAlertPriority: [],
  planningBufferZone: [],
  executionBufferZone: []
};

export interface IActivePlanner {
  userId: string;
  login: string;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  email?: string;
  connected: boolean;
  supplierIds: string[];
  userType: TUserType;
}

export interface IPOWorkshopCapacity {
  buffer: IBuffer;
  capacity?: number;
  capacityBufferRatio?: number;
  capacityGroup?: number;
  capacityUnit?: string;
  capacityUnitFrequency?: TAduFrequency;
  endPeriod: string;
  projectedBuffer: IBuffer;
  projectedReorderQty: number;
  reorderQty: number;
  startPeriod: string;
  workshopId: string;
}

interface IPOLifecycleEvent {
  poIds: string[];
  skuIds: string[];
}

type TDateField = 'dueDate' | 'deliveryDate' | 'reorderDate';

/**
 * Service centralising all things related to the PO API
 * It's the central source of truth for the PO workbench management
 */
export default class PlanningOrderApi extends Service {
  @service store!: StoreService;
  @service intl!: IntlService;
  @service bvFlash!: BvFlashService;
  @service eventStream!: EventStreamService;
  @service sessionUser!: SessionUserService;
  @service labelService!: LabelService;
  @service router!: RouterService;

  @tracked planningOrders: PlanningOrder[] = [];
  @tracked filteredPlanningOrders: PlanningOrder[] = [];
  @tracked workshops: IPOWorkshopCapacity[] = [];
  @tracked users: IActivePlanner[] = [];
  @tracked projection: PlanningOrder[] = [];
  @tracked selectedUserId: string = this.sessionUser.userId;
  @tracked selectedDateField: TDateField = 'dueDate';
  @tracked addedPOCount: number = 0;
  @tracked simulatedQuantity: number = 0;
  @tracked calculatingSkus: string[] = [];

  currentWorkshopId?: string;

  // Getters
  // ~~~~~~

  @cached
  get groupedPlanningOrders() {
    const { workshops } = this;
    const groups: IPOGroup[] = [];

    const plos = this.filteredPlanningOrders;
    const keys = plos
      .map((po) => po.orderKeyObject)
      .uniqBy('orderKey')
      .sortBy('orderKey');

    if (plos && plos.length) {
      const groupedByKey: Record<string, IPOGroup> = {};

      keys.forEach((keyObject) => {
        const workshopId = keyObject.workshop?.workshopId;
        let capacity: IPOWorkshopCapacity[] = [];
        if (workshops && workshopId) {
          capacity = workshops.filter((w) => w.workshopId === workshopId);
        }

        groupedByKey[keyObject.orderKey] = {
          keyObject,
          orderKey: keyObject.orderKey,
          orderHash: keyObject.orderHash,
          children: [],
          capacity: capacity?.sortBy('startPeriod')
        } as IPOGroup;
      });

      plos.forEach((plo) => {
        const key = plo.orderKey;
        if (groupedByKey[key]) {
          groupedByKey[key].children.pushObject(plo);
        }
      });

      Object.keys(groupedByKey).forEach((key) => {
        if (groupedByKey.hasOwnProperty(key)) {
          groups.push(groupedByKey[key]);
        }
      });
    }

    return groups;
  }

  get isSupplierUser() {
    return Boolean(this.sessionUser.user?.isSupplier);
  }

  /**
   * List of order numbers. It is used to populate the PO order numbers filters.
   */
  @cached
  get orderNumOptions() {
    return this.planningOrders
      .reduce((acc: string[], current) => {
        if (current.orderKeyObject.num && !acc.includes(current.orderKeyObject.num)) {
          acc.push(current.orderKeyObject.num);
        }
        return acc;
      }, [])
      .sort();
  }

  /**
   * List of statuses. It is used to populate the app menu items and status workflow tabs.
   */
  @cached
  get statusOptions() {
    const opts = getStatusOptions(this.planningOrders);
    return this.isSupplierUser ? opts.filter((o) => o.key !== 'DRAFT') : opts;
  }

  /**
   * List of order types. It is used to populate the PO order types filters.
   */
  @cached
  get orderTypeOptions() {
    return this.planningOrders
      .reduce((types: string[], po) => {
        if (po.orderType && !types.includes(po.orderType)) {
          types.push(po.orderType);
        }
        return types;
      }, [])
      .sort();
  }

  @cached
  get distinctSkuIds() {
    return new Set(this.planningOrders.map((po) => po.skuId));
  }

  /**
   * Get the selected user model from the PO users list
   */
  @cached
  get selectedUser() {
    return this.users.find((u) => this.selectedUserId === u.userId);
  }

  /**
   * Checks whether the POs, or the PO users are being fetched from the API
   * @type {boolean}
   */
  get isLoading() {
    return this.planningOrdersTask.isRunning ||
      this.usersTask.isRunning ||
      this.workshopsTask.isRunning;
  }

  // Lifecycle functions
  // ~~~~~~

  constructor() {
    super(...arguments);
    this.addEventHandlers();
  }

  // Actions
  // ~~~~~~~~

  @action
  filterPlanningOrders(filters?: IPOFilters) {
    this.filteredPlanningOrders =
      !this.planningOrders || !this.planningOrders.length
        ? []
        : filters
          ? this.applyFilters(filters)
          : this.filteredPlanningOrders;
  }

  @action
  onItemDeleted(plo: PlanningOrder) {
    this.removeById(plo.id);
  }

  /**
   * Adds a projected planned order to the projection collection
   * @param {PlanningOrder} po The projected planned order
   */
  @action
  addProjection(po: PlanningOrder) {
    if (!po || !this.isPoAuthorized(po) || !this.projection.includes(po)) {
      return;
    }

    if (this.projection) {
      this.projection = [...this.projection, po];
    } else {
      this.projection = [po];
    }
  }

  @action
  removeProjection(po: PlanningOrder) {
    this.removeProjectionById(po.id);
  }

  /**
   * This function sets the event handling functions
   * when the service is first instanciated
   */
  addEventHandlers() {
    const plCalcDoneEvents = [PlanningEvents.PlanningsCalculationDone];
    this.eventStream.addHandler(
      ResourceName.plural,
      CreatedEvents,
      this._onPlanningOrderCreated,
      this
    );
    this.eventStream.addHandler(
      ResourceName.plural,
      DeletedEvents,
      this._onPlanningOrderDeleted,
      this
    );
    this.eventStream.addHandler(
      ResourceName.plural,
      [POEvents.PlanningOrdersDeleted],
      this._onPlanningOrdersDeleted,
      this
    );
    this.eventStream.addHandler(
      ResourceName.plural,
      [POEvents.PlanningOrdersFinished],
      this._onPlanningOrdersFinished,
      this
    );
    this.eventStream.addHandler(
      ResourceName.plural,
      POEvents.PlanningOrderExternalDataChanged,
      this._onPlanningOrderExternalDataChanged,
      this
    );
    this.eventStream.addHandler(
      ResourceName.plural,
      POsLifecycleEvents,
      this._onPlanningOrdersStatusChanged,
      this
    );
    this.eventStream.addHandler(
      PlanningResourceName.plural,
      plCalcDoneEvents,
      this._onPlanningsCalculationDone,
      this
    );
    this.eventStream.addHandler(
      PlanningResourceName.plural,
      [PlanningEvents.PlanningsCalculationStarted],
      this._onPlanningsCalculationStarted,
      this
    );
  }

  teardown() {
    this.eventStream.removeHandlers(this);
  }

  resetCount() {
    this.addedPOCount = 0;
  }

  /**
   * Helper function for comparing 2 dates. Used in the filtering of the PO collecion.
   * @param {Date} date start date
   * @param {Date} dateFilter date to compare
   * @param {string} comparator Filter comparator: eq, lt or gt
   */
  compareDate(date: Date, dateFilter: Date, comparator?: 'eq' | 'lt' | 'gt') {
    const start = moment(date).format('YYYYMMDD');
    const end = moment(dateFilter).format('YYYYMMDD');
    switch (comparator) {
      case 'eq':
        return start === end;
      case 'lt':
        return start < end;
      case 'gt':
        return start > end;
      default:
        return false;
    }
  }

  /**
   * Adds a PO to the user PO workbench.
   * It first checks that the PO was created by the current user
   * and that it doesn't already exists before inserting it
   *
   * @param po The PO to add to the Workbench
   */
  add(po: PlanningOrder) {
    if (!po || !this.isPoAuthorized(po)) {
      return;
    }

    const poExists = this.planningOrders.find((p) => p.id === po.id);
    if (poExists) {
      return;
    }

    if (this.planningOrders) {
      this.planningOrders = [...this.planningOrders, po];
    } else {
      this.planningOrders = [po];
    }
    this.filterPlanningOrders();
  }

  /**
   * Adds multiple POs to the user PO workbench.
   * It first checks that the PO was created by the current user
   * and that it doesn't already exists before inserting it
   *
   * @param {Array<PlanningOrder>} pos The POs to add to the Workbench
   */
  addMany(pos: PlanningOrder[] = []) {
    if (pos.length) {
      const arr = [...this.planningOrders];
      const arrIds = arr.map((p) => p.id);
      pos.forEach((po) => {
        if (this.isPoAuthorized(po) && !arrIds.includes(po.id)) {
          arr.push(po);
        }
      });
      this.planningOrders = arr;
      this.filterPlanningOrders();
    }
  }

  /**
   * Removes a PO from the collection by id
   *
   * @param {id} id PO id to remove
   */
  removeById(id: string) {
    if (id && this.planningOrders.length) {
      this.planningOrders = this.planningOrders.filter((po) => po.id !== id);
      this.filterPlanningOrders();
    }
  }

  /**
   * Removes POs from the collection by ids
   * @param {Array<string>} ids PO ids to remove
   */
  removeByIds(ids: string[] = []) {
    if (ids.length && this.planningOrders.length) {
      this.planningOrders = this.planningOrders.filter((po) => !ids.includes(po.id));
      this.filterPlanningOrders();
    }
  }

  /**
   * Removes PO projections from the collection by id
   * @param {string} id Projected PO id to remove
   */
  removeProjectionById(id: string) {
    if (id && this.projection.length) {
      this.projection = this.projection.filter((po) => po.id !== id);
    }
  }

  /**
   * Switch PO workbench to the newly selected user
   * @param {User} user New selected user
   */
  switchUser(user: IActivePlanner | User) {
    if (user) {
      this.selectedUserId = user.userId || (user as User).id;
      this.refreshTask.perform();
    }
  }

  // Ember concurrency Tasks
  // ~~~~~~

  /**
   * This task will fetch the workshops and planning orders in parrallel
   * and wait for both tasks to finish before sending back the results
   * There's no need here for a try catch block as errors
   * from both called tasks are handled in the tasks themselves
   *
   * @param {Array<string>} skuIds SKU ids to refresh. If not defined, get all planning orders.
   */
  refreshTask = restartableTask(async (skuIds?: string[]) => {
    const tasks = [
      this.planningOrdersTask.perform(this.selectedUser ?? this.sessionUser.user, skuIds),
      this.workshopsTask.perform(),
      this.usersTask.perform()
    ];
    // @ts-ignore
    const result = await all(tasks);
    if (skuIds) {
      this.filteredPlanningOrders = [...this.planningOrders];
    }
    return result;
  });

  changeRoadmapTask = dropTask(async (roadmap: any = {}) => {
    if (!roadmap) {
      return;
    }

    const { intl } = this;
    const payload = { ...roadmap };

    if (payload.dueDate) {
      payload.dateField = 'dueDate'
      payload.date = moment(payload.dueDate).format('YYYY-MM-DD');
    }

    try {
      const response = await PlanningOrder.changeManyRoadmaps(payload);
      return response.data;
    } catch (error) {
      defaultErrorHandler(error);
      throw intl.t('poWorkbench.changeManyRoadmapsError');
    }
  });

  /**
   * Tasks for calling the `UpdateOrderNum` command
   *
   * @param {string} oldOrderNum Old order num
   * @param {string} newOrderNum New order num
   */
  updateOrderNumTask = dropTask(async (oldOrderNum: string, newOrderNum: string) => {
    try {
      const response: {
        data: IManyPlanningOrdersUpdatedResult;
      } = await PlanningOrder.updateOrderNum({
        oldOrderNum,
        newOrderNum
      });
      return response.data;
    } catch (error) {
      defaultErrorHandler(error);
      throw this.labelService.formValidationsFromApiError(error);
    }
  });

  /**
   * Tasks for calling the `UpdateExternalData` command
   *
   * @param {string} orderNum Order num
   * @param {string} newExternalOrderNum New external order num
   */
  updateExternalDataTask = dropTask(async (orderNum: string, newExternalOrderNum?: string) => {
    try {
      const response = await PlanningOrder.updateExternalData({
        orderNum,
        newExternalOrderNum
      });
      return response.data;
    } catch (error) {
      defaultErrorHandler(error);
      throw this.labelService.formValidationsFromApiError(error);
    }
  });

  launchMany(ids: string[]) {
    return this._changePOStatusTask.perform({
      cmdFn: PlanningOrder.launchMany.bind(PlanningOrder),
      ids,
      status: Statuses.LAUNCHED,
      refresh: false
    });
  }

  commitMany(ids: string[]) {
    return this._changePOStatusTask.perform({
      cmdFn: PlanningOrder.commitMany.bind(PlanningOrder),
      ids,
      status: Statuses.COMMITTED,
      refresh: false
    });
  }

  sendMany(ids: string[]) {
    return this._changePOStatusTask.perform({
      cmdFn: PlanningOrder.sendMany.bind(PlanningOrder),
      ids,
      status: Statuses.SENT,
      refresh: false
    });
  }

  @action
  setSelectedDateField(dateField: TDateField) {
    if (this.selectedDateField !== dateField) {
      this.selectedDateField = dateField;
      this.router.transitionTo({ queryParams: { dateField } });
      this.workshopsTask.perform();
    }
  }

  finishManyTask = dropTask(async (ids: string[]) => {
    return this._changePOStatusTask.perform({
      cmdFn: PlanningOrder.finishMany.bind(PlanningOrder),
      ids,
      status: Statuses.FINISHED,
      refresh: true
    });
  });

  deleteManyTask = dropTask(async (ids?: string[]) => {
    if (!ids || !ids.length) {
      return;
    }

    const { intl } = this;
    try {
      const response: { data: IManyPlanningOrdersUpdatedResult } = await PlanningOrder.deleteMany({
        ids
      });
      if (response.data && response.data.ok) {
        this.removeByIds(response.data.ok);
      }
      return response.data;
    } catch (error) {
      defaultErrorHandler(error);
      await this.refreshTask.perform();
      throw intl.t('poWorkbench.ploDeletionError');
    }
  });

  explodeManyTask = restartableTask(async (ids?: string[]) => {
    if (!ids || !ids.length) {
      return;
    }

    try {
      const response: { data: IManyPlanningOrdersUpdatedResult } = await PlanningOrder.explodeMany({
        ids
      });
      return response.data;
    } catch (error) {
      defaultErrorHandler(error);
      throw this.intl.t('poWorkbench.explodeManyError');
    }
  });

  /**
   * This function calls the CreateDraftOrder command from planning ids and assume
   * an event will be pushed to the websocket.
   * An event handler in this class will catch it and reload the created POs in the workbench
   * @param {Array<string>} planningIds A list of planning ids coming form the planning cards list
   */
  draftManyTask = dropTask(async (planningIds?: string[]) => {
    if (!planningIds || !planningIds.length) {
      return;
    }
    try {
      const response = await PlanningOrder.createDraftOrder({ planningIds });
      this.addedPOCount += planningIds.length;
      return response;
    } catch (error) {
      this.throwAssertOrFallback(error, 'poWorkbench.draftManyPloError');
      return;
    }
  });

  draftProjectionsTask = dropTask(async (ids?: string[], capacity?: IPOWorkshopCapacity) => {
    if (!ids || !ids.length) {
      return;
    }
    try {
      const payload = { ids, reorderDate: undefined as string | undefined };
      if (capacity) {
        const now = moment();
        const startPeriod = moment(capacity.startPeriod);
        payload.reorderDate = moment.max(now, startPeriod).format('YYYY-MM-DD');
      }
      const createdPOs: PlanningOrder[] = await PlanningOrder.draftProjections(payload);

      this.addMany(createdPOs);
      createdPOs.forEach((p) => this.removeProjectionById(p.id));
      this.addedPOCount += createdPOs.length;
      return createdPOs;
    } catch (error) {
      this.throwAssertOrFallback(error, 'poWorkbench.draftManyPloError');
      return;
    }
  });

  draftPlanningProjectionsTask = dropTask(async (skuId?: string, dates?: string[]) => {
    if (!skuId || !dates?.length) {
      return;
    }
    try {
      const createdPOs: PlanningOrder[] = await PlanningOrder.draftPlanningProjections({
        skuId,
        dates
      });
      this.addMany(createdPOs);
      createdPOs.forEach((p) => this.removeProjectionById(p.id));
      this.addedPOCount += createdPOs.length;
      return createdPOs;
    } catch (error) {
      this.throwAssertOrFallback(error, 'poWorkbench.draftManyPloError');
      return;
    }
  });

  _changePOStatusTask = task(async ({ cmdFn, ids, status, refresh }: any) => {
    if (!ids || !ids.length) {
      return;
    }

    try {
      const response: { data: IManyPlanningOrdersUpdatedResult } = await cmdFn({ ids });
      if (refresh) {
        await this.refreshTask.perform();
      }
      return response.data;
    } catch (error) {
      defaultErrorHandler(error);
      throw this.intl.t('poWorkbench.changePloStatusError', { status });
    }
  });

  /**
   * This task fetch users that have a planned order in their workbench
   */
  usersTask = restartableTask(async () => {
    try {
      const response: Ember.Array<IActivePlanner> = await this.store.customQuery(
        ResourceName.singular,
        Queries.Users
      );
      this.users = response ? response.toArray() : [];
      return this.users;
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });

  planningOrdersTask = restartableTask(async (selectedUser?: User | IActivePlanner, skuIds?: string[]) => {
    try {
      if (!selectedUser) {
        return;
      }
      const query: IJsonApiQuery = {
        page: {
          offset: 0,
          limit: 10000
        },
        filter: {
          status: niFilter(
            [
              Statuses.CANCELLED,
              Statuses.FINISHED,
              Statuses.NEW,
              Statuses.PROJECTED,
              Statuses.PROJECTED_FIRM
            ].concat(selectedUser?.userType === 'SUPPLIER' ? [Statuses.DRAFT] : [])
          )
        }
      };

      if (selectedUser.userType !== 'SUPPLIER') {
        (query.filter as Record<string, string>).createdBy = eqFilter(selectedUser.userId);
      }

      if (skuIds?.length && skuIds.length <= 20) {
        (query.filter as Record<string, string>).skuId = inFilter(skuIds);
      }

      const response: Ember.Array<PlanningOrder> = await this.store.query(
        ResourceName.singular,
        query
      );

      if (skuIds?.length) {
        this.addMany(response.toArray());
      } else {
        this.planningOrders = response.toArray();
      }
      return response;
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });

  projectionTask = restartableTask(async (
    poKeyObject: IPOKeyObject,
    groupOnly: boolean = false,
    capacity?: IPOWorkshopCapacity
  ) => {
    const workshopId = poKeyObject.workshop?.workshopId as string | undefined;
    if (!workshopId) {
      return;
    }

    try {
      const query = {
        page: {
          offset: 0,
          limit: 10000
        },
        sort: undefined as string | undefined,
        filter: {}
      };
      if (groupOnly) {
        const filter: Record<string, string | undefined> = {
          workshopId: eqFilter(workshopId),
          status: inFilter([Statuses.NEW, Statuses.PROJECTED]),
          reorderQty: gtFilter('0')
        };

        if (capacity) {
          const { warehouse, supplier, acqCode, deliveryWorkshop } = poKeyObject;
          const warehouseId = warehouse?.warehouseId;
          if (warehouseId) {
            filter.warehouseId = eqFilter(warehouseId);
          }

          const supplierId = supplier?.supplierId as string | undefined;
          if (supplierId) {
            filter.supplierId = eqFilter(supplierId);
          }

          const deliveryWorkshopId = deliveryWorkshop?.workshopId as string | undefined;

          if (deliveryWorkshopId) {
            filter.deliveryWorkshopId = eqFilter(deliveryWorkshopId);
          }

          const { startPeriod, endPeriod } = capacity;
          filter.reorderDate = btFilter([
            moment(startPeriod).format('YYYY-MM-DD'),
            moment(endPeriod).add(NB_PROJECTION_MONTHS, 'months').format('YYYY-MM-DD')
          ]);

          filter.acqCode = eqFilter(acqCode);
          query.sort = 'reorderDate';
        }

        query.filter = filter;
      } else {
        query.filter = {
          workshopId: eqFilter(workshopId),
          status: inFilter([Statuses.NEW, Statuses.PROJECTED, Statuses.PROJECTED_FIRM]),
          reorderQty: gtFilter('0'),
          dueDate: ltFilter(moment().add(NB_PROJECTION_MONTHS, 'months').format('YYYY-MM-DD'))
        };
      }
      const response: Ember.Array<PlanningOrder> = await this.store.query(
        ResourceName.singular,
        query
      );

      this.currentWorkshopId = workshopId;
      const projection = response.toArray();

      const selectedItems =
        groupOnly && capacity && !!projection.length
          ? this.filterProjectionsByCapacity(projection, capacity)
          : [];

      this.projection = projection;
      return { projection: this.projection, selectedItems };
    } catch (error) {
      if (workshopId !== this.currentWorkshopId) {
        this.projection = [];
        this.currentWorkshopId = undefined;
      }
      return defaultErrorHandler(error);
    }
  });

  filterProjectionsByCapacity(
    projection: PlanningOrder[],
    { capacity, reorderQty, capacityUnit }: IPOWorkshopCapacity
  ): PlanningOrder[] {
    if (capacity) {
      const quantityToFill = capacity - reorderQty;
      const toleranceCeiling = quantityToFill * 0.1;
      let reorderQtySum = 0;
      const _projection: PlanningOrder[] = [];

      for (const p of projection) {
        const cadence = capacityUnit === 'HOUR' ? p.cadence ?? 0 : p.cadence ?? 1;
        reorderQtySum += cadence > 0 ? p.reorderQty / cadence : 0;

        if (
          reorderQtySum <= quantityToFill ||
          (reorderQtySum > quantityToFill &&
            reorderQtySum <= quantityToFill + toleranceCeiling &&
            this.simulatedQuantity < quantityToFill)
        ) {
          this.simulatedQuantity = reorderQtySum;
          _projection.push(p);
        } else {
          break;
        }
      }

      return _projection;
    } else {
      return projection;
    }
  }

  workshopsTask = restartableTask(async (dateField?: TDateField) => {
    try {
      const response: Ember.Array<IPOWorkshopCapacity> = await this.store.customQuery(
        ResourceName.singular,
        Queries.WorkshopsCapacity,
        undefined,
        {
          dateField: dateField ?? this.selectedDateField
        }
      );
      this.workshops = response.toArray();
      return this.workshops;
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });

  alertsTask = restartableTask(async (poId?: string) => {
    try {
      if (!poId) {
        return [];
      }
      const query = {
        filter: {
          planningOrderId: eqFilter(poId)
        },
        page: {
          offset: 0,
          limit: 10000
        }
      };
      return await this.store.query(SyncAlertResourceName.singular, query);
    } catch (error) {
      return defaultErrorHandler(error);
    }
  });

  // Private Helpers
  // ~~~~~~~

  /**
   * Function fo filtering the planning orders collection with filters
   * @param {Object} filters PO Worbench filter values
   */
  private applyFilters({
                         dueDate,
                         sku,
                         status,
                         deliveryWorkshop,
                         supplier,
                         workshop,
                         acqCode,
                         orderNum,
                         orderType,
                         onlyPositiveQty,
                         dueDateOp,
                         syncAlert,
                         schedulingAlert,
                         schedulingAlertPriority,
                         planningBufferZone,
                         executionBufferZone
                       }: IPOFilters) {
    return this.planningOrders.filter((plo) => {
      return (
        (sku && sku !== ''
          ? plo.skuInfo.part.code.toLowerCase().indexOf(sku.toLowerCase()) > -1 ||
          `${plo.skuInfo.part.description}`.toLowerCase().indexOf(sku.toLowerCase()) > -1
          : true) &&
        (dueDate ? this.compareDate(plo[this.selectedDateField], dueDate, dueDateOp) : true) &&
        (status.length ? status.includes(plo.status) : true) &&
        (deliveryWorkshop.length
          ? deliveryWorkshop.includes(plo.deliveryWorkshopInfo?.workshopId as string)
          : true) &&
        (supplier.length ? supplier.includes(plo.supplierInfo?.supplierId as string) : true) &&
        (workshop.length ? workshop.includes(plo.workshopInfo?.workshopId as string) : true) &&
        (acqCode.length ? acqCode.includes(plo.acqCode) : true) &&
        (orderNum.length ? orderNum.includes(plo.num as string) : true) &&
        (onlyPositiveQty ? plo.reorderQty > 0 : true) &&
        (syncAlert.length
          ? syncAlert.includes(plo.alerts?.synchronisation?.criticality as number)
          : true) &&
        (schedulingAlert.length
          ? schedulingAlert.includes(plo.alerts?.scheduling?.criticality as number)
          : true) &&
        (schedulingAlertPriority.length
          ? (plo.alerts?.scheduling?.priority as number) >= schedulingAlertPriority[0] &&
          (plo.alerts?.scheduling?.priority as number) <= schedulingAlertPriority[1]
          : true) &&
        (orderType.length ? orderType.includes(plo.orderType as string) : true) &&
        (planningBufferZone.length
          ? planningBufferZone.includes(`${plo.planningInfo.bufferZone}`)
          : true) &&
        (executionBufferZone.length
          ? executionBufferZone.includes(`${plo.executionInfo.ohaZone}`)
          : true)
      );
    });
  }

  private _onPlanningOrderCreated(payload: DocWithData) {
    const createdPO = normalize(ResourceName.singular, payload) as PlanningOrder;
    this.add(createdPO);
  }

  private filterSkuIds(skuIds: string[], userId?: string, event?: string): string[] {
    if (
      event &&
      CREATION_EVENTS.includes(event) &&
      (this.isSupplierUser || userId === this.selectedUserId)
    ) {
      return skuIds;
    }
    const { distinctSkuIds } = this;
    return skuIds.filter((s) => distinctSkuIds.has(s));
  }

  private _onPlanningOrdersStatusChanged(
    { skuIds }: { skuIds: string[] },
    event: string,
    user: { id: string; login: string }
  ) {
    const poSkuids = this.filterSkuIds(skuIds, user.id, event);
    if (poSkuids.length) {
      this.refreshTask.perform(poSkuids);
    }
  }

  private _onPlanningOrderExternalDataChanged(payload: any) {
    const po = normalize(ResourceName.singular, payload) as PlanningOrder;
    this.refreshTask.perform([po.skuId]);
  }

  private _onPlanningOrderDeleted(payload: DocWithData) {
    const deletedPO = normalize(ResourceName.singular, payload) as PlanningOrder;
    this.removeById(deletedPO.id);
  }

  private _onPlanningOrdersDeleted(payload: IManyPlanningOrdersUpdatedResult) {
    this.removeByIds(payload.ok ?? []);
  }

  private _onPlanningOrdersFinished(payload: IPOLifecycleEvent) {
    this.removeByIds(payload.poIds ?? []);
  }

  private _onPlanningsCalculationDone(
    { skuIds, launchedBy }: IPlanningCalculationState,
    event: string
  ) {
    const poSkuids = this.filterSkuIds(skuIds, launchedBy?.id as string | undefined, event);
    if (poSkuids.length) {
      this.calculatingSkus = this.calculatingSkus.filter((s) => !poSkuids.includes(s));
      this.refreshTask.perform(poSkuids);
    }
  }

  private isPoAuthorized(po: PlanningOrder): boolean {
    const user = this.selectedUser ?? this.sessionUser.user;
    if (!user) {
      return false;
    }
    if (user.userType === 'SUPPLIER') {
      return Boolean(po.supplierId && user.supplierIds.includes(po.supplierId as string));
    } else {
      return po.createdBy === user.userId;
    }
  }

  private _onPlanningsCalculationStarted({ skuIds, launchedBy }: IPlanningCalculationState) {
    this.calculatingSkus = [
      ...new Set(this.calculatingSkus.concat(this.filterSkuIds(skuIds, launchedBy?.id as string | undefined)))
    ];
  }

  // Helpers
  // ~~~~~~~~

  private throwAssertOrFallback(error: IJSONAPIErrors, fallbackMessage: string) {
    defaultErrorHandler(error);
    const firstError = error.errors?.[0];
    if (firstError?.detail?.customAssert) {
      throw firstError.title;
    }
    throw this.intl.t(fallbackMessage);
  }
}

export const getStatusOptions = (plos: PlanningOrder[]) => {
  if (!plos) {
    return [];
  }

  type TOpt = Record<TPOStatus, { count: number; key: TPOStatus; label: string }>;
  const opts: TOpt = {} as TOpt;
  StatusesOptions.forEach((s) => {
    opts[s.key] = { ...s, count: 0 };
  });
  plos
    .filter((p) => VisibleStatuses.includes(p.status))
    .forEach((po) => {
      const { status } = po;
      const opt = opts[status];
      if (opt) {
        opt.count += 1;
      }
      opts[status] = opt;
    });
  return Object.values(opts);
};
